import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { defaultSiteSettings } from '@/lib/defaults';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function buildMailtoEnquiry() {
  const subject = encodeURIComponent('Singvolve Web Enquiry: Singing Lessons');
  const body = encodeURIComponent(
    'Name - Pupil: \n' +
    'Name - Parent/ Carer (If applicable): \n' +
    'Email: \n' +
    'Telephone number: \n' +
    'Are you available on a Tuesday or Thursday evening between 5-7pm to have a 30 minute lesson at the Bristol Cathedral Choir School (BCCS) in Bristol: \n' +
    'What style of music would you like to try (optional): '
  );
  return `mailto:${defaultSiteSettings.adminEmail}?subject=${subject}&body=${body}`;
}

export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">Contact</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">Get in Touch</h1>
          <p className="text-base sm:text-lg text-[#5E4B66]">
            Ready to start your singing journey? Have a question? We'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
            <Card className="border-2 border-[#4B1D5E] rounded-2xl shadow-lg">
              <CardContent className="p-8">
                <h3 className="font-heading text-2xl font-bold text-[#2D1B36] mb-4">Send an Enquiry</h3>
                <p className="text-sm text-[#5E4B66] mb-6 leading-relaxed">
                  Click the button below to open your email with all the details we need pre-filled. Just complete the fields and send!
                </p>

                <div className="bg-[#F5F0F7] rounded-xl p-6 mb-6">
                  <p className="text-sm font-medium text-[#4B1D5E] mb-3">Your email will include:</p>
                  <ul className="space-y-2 text-sm text-[#5E4B66]">
                    <li>Name - Pupil</li>
                    <li>Name - Parent/Carer (if applicable)</li>
                    <li>Email address</li>
                    <li>Telephone number</li>
                    <li>Availability (Tues/Thurs 5-7pm)</li>
                    <li>Preferred music style (optional)</li>
                  </ul>
                </div>

                <a href={buildMailtoEnquiry()} data-testid="contact-email-cta">
                  <Button className="w-full bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                    <Mail className="mr-2 h-5 w-5" /> Send Email Enquiry
                  </Button>
                </a>
                <p className="text-xs text-[#8A7E90] text-center mt-3">
                  This will open your default email application
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: 0.15 }} className="space-y-6">
            <Card className="border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center shrink-0">
                    <Mail className="h-6 w-6 text-[#4B1D5E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-[#2D1B36] mb-1">Email</h4>
                    <a href="mailto:catherine@singvolve.com" className="text-[#4B1D5E] hover:underline text-sm" data-testid="contact-email-link">
                      catherine@singvolve.com
                    </a>
                    <p className="text-xs text-[#8A7E90] mt-1">We typically respond within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6 text-[#4B1D5E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-[#2D1B36] mb-1">Location of Lessons</h4>
                    <p className="text-sm text-[#5E4B66]">Bristol Cathedral Choir School</p>
                    <p className="text-sm text-[#5E4B66]">College Square, Bristol, BS1 5TS</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center shrink-0">
                    <Clock className="h-6 w-6 text-[#4B1D5E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-[#2D1B36] mb-1">Lesson Times</h4>
                    <p className="text-sm text-[#5E4B66]">Tuesday & Thursday evenings</p>
                    <p className="text-sm text-[#5E4B66]">5:00 PM - 7:00 PM</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
